"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Upload } from "lucide-react";
import { AttachmentQuarantine } from "./attachment-quarantine";
import { Button } from ".";
import {
  api,
  formatBytes,
  reasonText,
  uploadAttachment,
  type AttachmentSummary
} from "../../lib/api";

const isImage = (row: AttachmentSummary) =>
  row.contentType === "image/jpeg" || row.contentType === "image/png";

/** 稿件附件。自 WO-107 起，ACTIVE 附件随稿件一起公开：图片作封面与插图，其余文件进公开附件目录。
    哪张是封面由服务端按公开规则给出（cover），这里只标出来，不自己猜。 */
export function ContentAttachments({
  ownerId,
  editable,
  disabled,
  onBusy,
  cover = null,
  onRows,
  onChanged
}: {
  ownerId: string;
  editable: boolean;
  disabled: boolean;
  onBusy: (busy: boolean) => void;
  /** 服务端按公开规则给出的封面；为空表示公开后版面不带图。 */
  cover?: { id: string; name: string } | null;
  /** 附件行读到后交给上层，预览据此画封面、插图与公开附件目录。 */
  onRows?: (rows: AttachmentSummary[] | null) => void;
  /** 上传或隔离之后通知上层重读详情，封面可能因此改变。 */
  onChanged?: () => void;
}) {
  const [rows, setRows] = useState<AttachmentSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const request = useRef(0);
  const uploadLock = useRef(false);
  const load = useCallback(async () => {
    const ticket = ++request.current;
    setFailure(null);
    try {
      const result = await api<{ items: AttachmentSummary[] }>(
        `/api/v1/attachments?${new URLSearchParams({ ownerType: "PORTAL_CONTENT", ownerId })}`
      );
      if (ticket === request.current) {
        setRows(result.items);
        onRows?.(result.items);
      }
    } catch (error) {
      if (ticket === request.current) {
        setRows(null);
        onRows?.(null);
        setFailure(reasonText(error));
      }
    }
  }, [ownerId, onRows]);
  useEffect(() => {
    void load();
    return () => {
      request.current++;
    };
  }, [load]);
  const attach = async (file: File) => {
    if (uploadLock.current || disabled || !editable) return;
    if (!file.size || file.size > 10 * 1024 * 1024) {
      setFailure(!file.size ? "不能上传空文件。" : "单个附件不能超过 10 MB。");
      return;
    }
    uploadLock.current = true;
    setUploading(true);
    onBusy(true);
    setMessage("");
    setFailure(null);
    try {
      const uploaded = await uploadAttachment({ ownerType: "PORTAL_CONTENT", ownerId, file });
      setMessage(`已上传：${uploaded.fileName}`);
      await load();
      onChanged?.();
    } catch (error) {
      setFailure(`${reasonText(error)}。如网络中断，请先重新读取附件列表，确认是否已上传。`);
    } finally {
      uploadLock.current = false;
      setUploading(false);
      onBusy(false);
    }
  };
  const describe = (row: AttachmentSummary) => {
    if (row.status === "QUARANTINED")
      return `已隔离，停止下载${row.quarantineReason ? `：${row.quarantineReason}` : ""}`;
    if (row.status === "SUPERSEDED") return "已替换，不再公开";
    if (isImage(row))
      return cover?.id === row.id ? "封面 · 公开后作首图与列表缩略图" : "图片 · 公开后排在正文后";
    return `${row.contentType} · 公开后进入附件目录`;
  };
  return (
    <section className="content-materials" aria-label="附件">
      <div className="content-section-title">
        <h3>
          <Paperclip size={16} />
          附件{rows !== null ? ` · ${rows.length}` : ""}
        </h3>
        {editable && (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled || uploading}
            onClick={() => fileInput.current?.click()}
          >
            <Upload size={14} />
            {uploading ? "正在上传…" : "添加附件"}
          </Button>
        )}
      </div>
      <p className="content-field-note">
        起草时上传，公开后随稿件一起公开：JPEG / PNG 里最早上传的一张作封面与首图，其余图片排在正文后，
        PDF / Word / Excel 进入公开附件目录；隔离或已替换的附件不公开。单个不超过 10 MB。
      </p>
      <input
        ref={fileInput}
        className="sr-only"
        type="file"
        aria-label="选择附件"
        disabled={disabled || uploading || !editable}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void attach(file);
        }}
      />
      {message && (
        <p className="content-success" role="status">
          {message}
        </p>
      )}
      {failure && (
        <div className="workspace-error" role="alert">
          {failure}
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => void load()}>
            重新读取附件
          </Button>
        </div>
      )}
      {rows === null && !failure ? (
        <p role="status" className="content-field-note">
          正在读取附件…
        </p>
      ) : rows?.length === 0 ? (
        <p className="content-field-note">尚无附件。公开后版面不带图，也没有附件目录。</p>
      ) : (
        <ul className="content-attachment-list">
          {rows?.map((row) => (
            <li key={row.id}>
              <div>
                {row.status === "ACTIVE" ? (
                  <a
                    href={`/api/v1/attachments/${encodeURIComponent(row.id)}/content`}
                    download={row.fileName}
                  >
                    {row.fileName}
                  </a>
                ) : (
                  <span>{row.fileName}</span>
                )}
                {row.status === "ACTIVE" && cover?.id === row.id && (
                  <span className="content-attachment-cover">封面</span>
                )}
                <small>
                  {formatBytes(row.byteSize)} · {describe(row)}
                </small>
              </div>
              <AttachmentQuarantine
                file={row}
                ownerType="PORTAL_CONTENT"
                disabled={disabled || uploading}
                onSaved={(file) => {
                  const next = rows?.map((item) => (item.id === file.id ? file : item)) ?? null;
                  setRows(next);
                  onRows?.(next);
                  onChanged?.();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
